import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
} from "@mui/material";
import { format } from "date-fns";
import FilterSelect from "../../../../components/FilterSelect";

export default React.memo(function ReservationDialog({
  open,
  reservation,   // <— newly added
  offer,         // existing (for “reserve from an offer”)
  onSave,
  onClose,
}) {
  const todayStr = format(new Date(), "yyyy-MM-dd");

  // Initialize with empty/new defaults
  const [data, setData] = useState({
    reservationType: "",
    company: "",
    reservationDate: todayStr,
    validityDate: todayStr,
    numberOfCases: "",
    netWeightPerCase: "",
    quantity: "",
    volumeUnit: "KG",
    remark: "",
  });

  // Whenever we get a reservation (edit) or offer (new), seed state:
  useEffect(() => {
    if (reservation) {
      // edit mode
      setData({
        reservationType: reservation.reservationType ?? "",
        company: reservation.company ?? "",
        // strip time portion if present
        reservationDate: reservation.reservationDate?.split("T")[0] ?? todayStr,
        validityDate:   reservation.validityDate?.split("T")[0]   ?? todayStr,
        numberOfCases:  reservation.numberOfCases  ?? "",
        netWeightPerCase: reservation.netWeightPerCase ?? "",
        quantity:       reservation.quantity       ?? "",
        volumeUnit:     reservation.volumeUnit     ?? "KG",
        remark:         reservation.remark         ?? "",
        recordStatus:  reservation.recordStatus         ?? "ACTIVE",
      });
    } else if (offer) {
      // new-reservation mode
      setData({
        reservationType: offer.offerType ?? "",
        company:         offer.company   ?? "",
        reservationDate: todayStr,
        validityDate:    todayStr,
        numberOfCases:   offer.numberOfCases ?? "",
        netWeightPerCase:offer.netWeightPerCase ?? "",
        quantity:        offer.quantity ?? "",
        volumeUnit:      "KG",
        remark:          "",
      });
    }
  }, [reservation, offer, todayStr]);

  // recompute quantity if both inputs present
  useEffect(() => {
    if (data.numberOfCases && data.netWeightPerCase) {
      const qty =
        Number(data.numberOfCases) * Number(data.netWeightPerCase);
      setData(d => ({ ...d, quantity: qty }));
    }
  }, [data.numberOfCases, data.netWeightPerCase]);

  const handleChange = field => e => {
    setData(d => ({ ...d, [field]: e.target.value }));
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {reservation
          ? "Edit Reservation"
          : offer?.offerType === "VENDOR"
          ? "Reserve for Purchase" + " " + (offer?.itemId ||"") + " - "+( offer?.itemBuyingGrade || "")
          : "Reserve for Sale"}
      </DialogTitle>
      <DialogContent dividers>
      
      <FilterSelect
                        label="Company"
                        menuType="companies" 
                        value={data?.company}
                        onChange={handleChange("company")}
                        fullWidth
                      />
      
        {/* Reservation Date */}
        <TextField
          margin="dense"
          label="Reservation Date"
          type="date"
          fullWidth
          onKeyDown={(e) => e.preventDefault()}
          InputLabelProps={{ shrink: true }}
          value={data.reservationDate}
          onChange={handleChange("reservationDate")}
        />

        {/* Validity Date */}
        <TextField
          margin="dense"
          label="Validity Date"
          type="date"
          fullWidth
          onKeyDown={(e) => e.preventDefault()}
          InputLabelProps={{ shrink: true }}
          value={data.validityDate}
          onChange={handleChange("validityDate")}
        />

        {/* Number of Cases */}
        <TextField
          margin="dense"
          label="Number of Cases"
          type="number"
          fullWidth
          value={data.numberOfCases}
          onChange={handleChange("numberOfCases")}
        />

        {/* Net Weight per Case */}
        <TextField
          margin="dense"
          label="Net Weight per Case"
          type="number"
          fullWidth
          value={data.netWeightPerCase}
          onChange={handleChange("netWeightPerCase")}
        />

        {/* Quantity */}
        <TextField
          margin="dense"
          label="Quantity"
          type="number"
          fullWidth
          value={data.quantity}
          onChange={handleChange("quantity")}
        />

        {/* Volume Unit */}
        <TextField
          margin="dense"
          label="Unit"
          select
          fullWidth
          value={data.volumeUnit}
          onChange={handleChange("volumeUnit")}
        >
          {["KG", "TON"].map(u => (
            <MenuItem key={u} value={u}>
              {u}
            </MenuItem>
          ))}
        </TextField>

        {/* Remark */}
        <TextField
          margin="dense"
          label="Remark"
          multiline
          rows={3}
          fullWidth
          value={data.remark}
          onChange={handleChange("remark")}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => onSave(data)}
          disabled={
            !data.numberOfCases ||
            !data.netWeightPerCase
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
});
